import React, { useEffect, useRef } from 'react';
export const About = ({
  onCursorChange
}) => {
  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const textRefs = useRef([]);
  const scanRef = useRef(null);
  // Handle mouse movement for 3D image effect
  const handleMouseMove = e => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const moveX = (e.clientX - centerX) / 20;
    const moveY = (e.clientY - centerY) / 20;
    imageRef.current.style.transform = `perspective(1000px) rotateX(${-moveY * 0.5}deg) rotateY(${moveX * 0.5}deg)`;
    // Move scan effect
    if (scanRef.current) {
      scanRef.current.style.transform = `translateY(${moveY * 5}px)`;
    }
  };
  useEffect(() => {
    // Observer for text animation on scroll
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, {
      threshold: 0.1
    });
    // Observe text elements
    textRefs.current.forEach(el => {
      if (el) observer.observe(el);
    });
    // Add mouse move listener for 3D effect
    if (containerRef.current) {
      containerRef.current.addEventListener('mousemove', handleMouseMove);
    }
    return () => {
      textRefs.current.forEach(el => {
        if (el) observer.unobserve(el);
      });
      if (containerRef.current) {
        containerRef.current.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);
  return <section id="about" ref={containerRef} className="w-full py-20 relative overflow-hidden" data-id="element-48">
      <style jsx global data-id="element-49">{`
        .about-text {
          opacity: 0;
        }
        .about-text.animate-fade-in-up {
          animation-delay: 0.2s;
        }
        .about-image {
          transition: transform 0.3s ease-out;
          transform-style: preserve-3d;
          box-shadow: 0 0 30px rgba(111, 248, 240, 0.2);
        }
        @keyframes scan {
          0% {
            top: -10%;
          }
          100% {
            top: 100%;
          }
        }
        .scan-effect {
          position: absolute;
          height: 10%;
          width: 100%;
          background: linear-gradient(
            to bottom,
            rgba(111, 248, 240, 0) 0%,
            rgba(111, 248, 240, 0.5) 50%,
            rgba(111, 248, 240, 0) 100%
          );
          animation: scan 2.5s linear infinite;
          opacity: 0;
          transition:
            opacity 0.3s ease,
            transform 0.3s ease;
        }
        .about-image:hover .scan-effect {
          opacity: 1;
        }
        .tag {
          transition: all 0.3s ease;
          opacity: 0;
          transform: translateY(10px);
          box-shadow: 0 0 10px rgba(111, 248, 240, 0.2);
        }
        .tag.animate-fade-in-up {
          animation-delay: calc(0.1s * var(--index));
        }
        .tag:hover {
          box-shadow: 0 0 15px rgba(111, 248, 240, 0.4);
        }
        .about-glow {
          position: absolute;
          inset: -50%;
          background: radial-gradient(
            circle,
            rgba(111, 248, 240, 0.1) 0%,
            rgba(111, 248, 240, 0) 70%
          );
          opacity: 0;
          transition: opacity 0.5s ease;
          pointer-events: none;
        }
        .about-image:hover .about-glow {
          opacity: 1;
        }
        .tech-data-point {
          position: absolute;
          padding: 4px 8px;
          background: rgba(3, 5, 8, 0.7);
          border: 1px solid rgba(111, 248, 240, 0.3);
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.65rem;
          color: #6ff8f0;
          white-space: nowrap;
          opacity: 0;
          transition: opacity 0.5s ease;
          z-index: 10;
        }
        .tech-data-point::before {
          content: '';
          position: absolute;
          width: 20px;
          height: 1px;
          background: rgba(111, 248, 240, 0.5);
        }
        .about-image:hover .tech-data-point {
          opacity: 1;
        }
        .tech-data-1 {
          top: 20%;
          left: -10px;
          transform: translateX(-100%);
        }
        .tech-data-1::before {
          top: 50%;
          right: -20px;
        }
        .tech-data-2 {
          bottom: 30%;
          right: -10px;
          transform: translateX(100%);
        }
        .tech-data-2::before {
          top: 50%;
          left: -20px;
        }
        .tech-data-3 {
          top: 10%;
          right: -10px;
          transform: translateX(100%);
        }
        .tech-data-3::before {
          top: 50%;
          left: -20px;
        }
        .technical-grid {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image:
            linear-gradient(
              to right,
              rgba(111, 248, 240, 0.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              to bottom,
              rgba(111, 248, 240, 0.05) 1px,
              transparent 1px
            );
          background-size: 20px 20px;
          opacity: 0.2;
          pointer-events: none;
        }
        /* Clay Bust styles */
        .clay-bust-container {
          width: 120px;
          height: 150px;
          margin: 0 auto 20px;
          perspective: 800px;
          position: relative;
        }
        .clay-bust-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          animation: rotate-clay-bust 12s infinite linear;
        }
        @keyframes rotate-clay-bust {
          0% {
            transform: rotateY(0) rotateX(5deg);
          }
          100% {
            transform: rotateY(360deg) rotateX(5deg);
          }
        }
        /* Holographic entity base */
        .entity-base {
          position: absolute;
          width: 60px;
          height: 70px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 40% 40% 30% 30% / 60% 60% 40% 40%;
          top: 20px;
          left: 50%;
          transform: translateX(-50%) translateZ(5px);
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.4);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          overflow: hidden;
        }
        /* Holographic head */
        .entity-head {
          position: absolute;
          width: 40px;
          height: 45px;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 50% 50% 40% 40% / 60% 60% 40% 40%;
          top: 0;
          left: 50%;
          transform: translateX(-50%) translateZ(10px);
          box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
          overflow: hidden;
          animation: float-subtle 4s infinite ease-in-out;
        }
        /* Angelic wings */
        .entity-wing {
          position: absolute;
          height: 80px;
          width: 40px;
          background: linear-gradient(
            to bottom,
            rgba(255, 255, 255, 0.9),
            rgba(255, 255, 255, 0.3)
          );
          top: 20px;
          transform-origin: top center;
          transform-style: preserve-3d;
          filter: blur(1px);
          z-index: -1;
        }
        .entity-wing.left {
          left: 5px;
          transform: translateZ(0px) rotateY(-30deg) rotateX(20deg);
          animation: wing-flap-left 6s infinite ease-in-out;
        }
        .entity-wing.right {
          right: 5px;
          transform: translateZ(0px) rotateY(30deg) rotateX(20deg);
          animation: wing-flap-right 6s infinite ease-in-out;
        }
        @keyframes wing-flap-left {
          0%,
          100% {
            transform: translateZ(0px) rotateY(-30deg) rotateX(20deg);
          }
          50% {
            transform: translateZ(0px) rotateY(-40deg) rotateX(25deg);
          }
        }
        @keyframes wing-flap-right {
          0%,
          100% {
            transform: translateZ(0px) rotateY(30deg) rotateX(20deg);
          }
          50% {
            transform: translateZ(0px) rotateY(40deg) rotateX(25deg);
          }
        }
        /* Feather details */
        .entity-feather {
          position: absolute;
          background: rgba(255, 255, 255, 0.8);
          height: 2px;
          transform-origin: left center;
        }
        /* Wolf essence */
        .wolf-essence {
          position: absolute;
          width: 50px;
          height: 30px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 60% 60% 40% 40% / 60% 60% 40% 40%;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%) translateZ(5px);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
          overflow: hidden;
        }
        .wolf-head {
          position: absolute;
          width: 25px;
          height: 20px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 60% 60% 40% 40% / 60% 60% 40% 40%;
          top: 5px;
          left: 50%;
          transform: translateX(-50%) translateZ(2px);
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
        }
        /* Halo effect */
        .entity-halo {
          position: absolute;
          width: 30px;
          height: 5px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.8);
          top: -10px;
          left: 50%;
          transform: translateX(-50%) translateZ(15px) rotateX(70deg);
          box-shadow: 0 0 20px rgba(255, 255, 255, 0.9);
          filter: blur(1px);
          animation: halo-pulse 4s infinite ease-in-out;
        }
        @keyframes halo-pulse {
          0%,
          100% {
            opacity: 0.8;
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.9);
          }
          50% {
            opacity: 1;
            box-shadow: 0 0 30px rgba(255, 255, 255, 1);
          }
        }
        /* Energy lines */
        .energy-line {
          position: absolute;
          height: 100%;
          width: 2px;
          background: linear-gradient(
            to bottom,
            transparent,
            rgba(230, 76, 255, 0.8),
            transparent
          );
          filter: blur(1px);
          animation: pulse-subtle 3s infinite ease-in-out;
          z-index: 2;
        }
        .energy-line.pink-1 {
          left: 30%;
          top: 0;
          height: 100%;
          animation-delay: 0.2s;
        }
        .energy-line.pink-2 {
          right: 40%;
          top: 0;
          height: 70%;
          animation-delay: 0.5s;
        }
        .energy-line.blue {
          left: 60%;
          top: 10%;
          height: 80%;
          background: linear-gradient(
            to bottom,
            transparent,
            rgba(111, 248, 240, 0.8),
            transparent
          );
          animation-delay: 0.8s;
        }
        /* Circular energy patterns */
        .energy-circle {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(230, 76, 255, 0.5);
          box-shadow: 0 0 10px rgba(230, 76, 255, 0.3);
          animation: pulse-subtle 4s infinite ease-in-out;
        }
        .energy-circle.circle-1 {
          width: 15px;
          height: 15px;
          top: 40%;
          left: 30%;
          animation-delay: 0.3s;
        }
        .energy-circle.circle-2 {
          width: 10px;
          height: 10px;
          bottom: 30%;
          right: 35%;
          border-color: rgba(111, 248, 240, 0.5);
          box-shadow: 0 0 10px rgba(111, 248, 240, 0.3);
          animation-delay: 0.7s;
        }
        /* Holographic glow */
        .holographic-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(
            ellipse at center,
            rgba(255, 255, 255, 0.1) 0%,
            rgba(111, 248, 240, 0.05) 40%,
            rgba(230, 76, 255, 0.05) 60%,
            transparent 100%
          );
          mix-blend-mode: screen;
          opacity: 0.8;
          animation: glow-shift 8s infinite alternate;
        }
        @keyframes glow-shift {
          0% {
            opacity: 0.6;
            filter: hue-rotate(0deg);
          }
          100% {
            opacity: 0.9;
            filter: hue-rotate(30deg);
          }
        }
        /* Spear/staff */
        .entity-spear {
          position: absolute;
          width: 2px;
          height: 100px;
          background: rgba(255, 255, 255, 0.8);
          top: 30px;
          left: 80%;
          transform: translateZ(5px) rotate(20deg);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
        }
        .spear-tip {
          position: absolute;
          width: 8px;
          height: 15px;
          background: rgba(255, 255, 255, 0.9);
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.7);
        }
        /* Floating particles */
        .entity-particle {
          position: absolute;
          width: 3px;
          height: 3px;
          background: rgba(255, 255, 255, 0.8);
          border-radius: 50%;
          animation: float-particle 10s infinite linear;
        }
        @keyframes float-particle {
          0% {
            transform: translate(0, 0);
            opacity: 0;
          }
          10% {
            opacity: 0.8;
          }
          90% {
            opacity: 0.8;
          }
          100% {
            transform: translate(var(--x, 20px), var(--y, -30px));
            opacity: 0;
          }
        }
        /* Shoulders and arm joints */
        .clay-shoulder {
          position: absolute;
          width: 20px;
          height: 15px;
          background: linear-gradient(135deg, #d5d5d5 0%, #a8a8a8 100%);
          border-radius: 50%;
          bottom: 25px;
          box-shadow:
            inset -3px -3px 6px rgba(0, 0, 0, 0.2),
            inset 3px 3px 6px rgba(255, 255, 255, 0.15),
            2px 2px 5px rgba(0, 0, 0, 0.25);
        }
        .clay-shoulder.left {
          left: 10px;
          transform: translateZ(5px);
          animation: left-shoulder-movement 3s ease-in-out infinite alternate;
        }
        .clay-shoulder.right {
          right: 10px;
          transform: translateZ(5px);
          animation: right-shoulder-movement 3s ease-in-out infinite alternate;
        }
        @keyframes left-shoulder-movement {
          0% {
            transform: translateZ(5px) rotateX(0deg);
          }
          100% {
            transform: translateZ(5px) rotateX(20deg) rotateZ(10deg);
          }
        }
        @keyframes right-shoulder-movement {
          0% {
            transform: translateZ(5px) rotateX(0deg);
          }
          100% {
            transform: translateZ(5px) rotateX(20deg) rotateZ(-10deg);
          }
        }
        .clay-arm {
          position: absolute;
          width: 10px;
          height: 30px;
          background: linear-gradient(135deg, #d5d5d5 0%, #a8a8a8 100%);
          border-radius: 10px;
          top: 10px;
          box-shadow:
            inset -2px -2px 4px rgba(0, 0, 0, 0.2),
            inset 2px 2px 4px rgba(255, 255, 255, 0.15),
            2px 2px 5px rgba(0, 0, 0, 0.25);
        }
        .clay-arm.left {
          left: 5px;
          transform: translateZ(5px) rotate(10deg);
          animation: left-arm-movement 4s ease-in-out infinite alternate;
        }
        .clay-arm.right {
          right: 5px;
          transform: translateZ(5px) rotate(-10deg);
          animation: right-arm-movement 4s ease-in-out infinite alternate;
        }
        @keyframes left-arm-movement {
          0% {
            transform: translateZ(5px) rotate(10deg);
          }
          100% {
            transform: translateZ(5px) rotate(30deg);
          }
        }
        @keyframes right-arm-movement {
          0% {
            transform: translateZ(5px) rotate(-10deg);
          }
          100% {
            transform: translateZ(5px) rotate(-30deg);
          }
        }
        .clay-joint {
          position: absolute;
          width: 12px;
          height: 12px;
          background: linear-gradient(135deg, #d5d5d5 0%, #a8a8a8 100%);
          border-radius: 50%;
          box-shadow:
            inset -2px -2px 4px rgba(0, 0, 0, 0.2),
            inset 2px 2px 4px rgba(255, 255, 255, 0.15),
            1px 1px 3px rgba(0, 0, 0, 0.25);
        }
        .clay-joint.neck {
          top: 45px;
          left: 50%;
          transform: translateX(-50%) translateZ(6px);
          z-index: 2;
        }
        .clay-joint.left-shoulder {
          top: 0;
          left: 50%;
          transform: translateX(-50%) translateZ(6px);
        }
        .clay-joint.right-shoulder {
          top: 0;
          left: 50%;
          transform: translateX(-50%) translateZ(6px);
        }
        /* Concrete texture */
        .concrete-texture {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.5' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.1'/%3E%3C/svg%3E");
          opacity: 0.8;
          border-radius: inherit;
          mix-blend-mode: overlay;
        }
        .concrete-grain {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 2px,
            rgba(0, 0, 0, 0.05) 2px,
            rgba(0, 0, 0, 0.05) 4px
          );
          border-radius: inherit;
          mix-blend-mode: multiply;
        }
        /* 3D print layer lines */
        .print-layers {
          position: absolute;
          width: 100%;
          height: 100%;
          background-image: repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0, 0, 0, 0.05) 2px,
            rgba(0, 0, 0, 0.05) 3px
          );
          border-radius: inherit;
          opacity: 0.7;
          mix-blend-mode: multiply;
        }
        /* Concrete cracks and imperfections */
        .concrete-crack {
          position: absolute;
          background: rgba(0, 0, 0, 0.15);
          z-index: 2;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        }
        .concrete-chip {
          position: absolute;
          width: 4px;
          height: 4px;
          background: linear-gradient(
            135deg,
            rgba(0, 0, 0, 0.2),
            rgba(0, 0, 0, 0.1)
          );
          border-radius: 50%;
          box-shadow: inset 1px 1px 1px rgba(0, 0, 0, 0.2);
        }
        /* Joint axis indicators */
        .joint-axis {
          position: absolute;
          width: 1px;
          height: 8px;
          background: rgba(0, 0, 0, 0.4);
          z-index: 10;
          box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
        }
        .joint-axis.vertical {
          transform: rotateZ(0deg);
        }
        .joint-axis.horizontal {
          transform: rotateZ(90deg);
        }
        .joint-axis.diagonal-right {
          transform: rotateZ(45deg);
        }
        .joint-axis.diagonal-left {
          transform: rotateZ(-45deg);
        }
        /* Michelangelo-style details */
        .classical-detail {
          position: absolute;
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.2),
            rgba(255, 255, 255, 0.05)
          );
          z-index: 2;
          border-radius: 50%;
          box-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
        }
        /* 3D shadows for depth */
        .shadow-cast {
          position: absolute;
          background: radial-gradient(
            ellipse at center,
            rgba(0, 0, 0, 0.2) 0%,
            transparent 70%
          );
          border-radius: 50%;
          z-index: -1;
          opacity: 0.5;
        }
        .head-shadow {
          width: 60px;
          height: 10px;
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%) rotateX(60deg);
        }
        .arm-shadow {
          width: 15px;
          height: 5px;
          bottom: -3px;
          left: 50%;
          transform: translateX(-50%) rotateX(60deg);
        }
        /* Highlights for 3D effect */
        .highlight {
          position: absolute;
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.4),
            transparent
          );
          border-radius: inherit;
          opacity: 0.3;
          mix-blend-mode: overlay;
        }
        .highlight-top {
          top: 0;
          left: 0;
          right: 30%;
          bottom: 60%;
          border-radius: 60% 40% 0 0;
        }
      `}</style>
      {/* Background effects */}
      <div className="absolute inset-0 bg-[#020305]" data-id="element-50"></div>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-30 mix-blend-overlay" data-id="element-51"></div>
      <div className="absolute inset-0 bg-[url('https://upload.wikimedia.org/wikipedia/commons/thumb/9/9f/Haeckel_Nepenthaceae.jpg/800px-Haeckel_Nepenthaceae.jpg')] bg-center bg-no-repeat bg-cover opacity-5 mix-blend-color-burn" data-id="element-52"></div>
      <div className="technical-grid" data-id="element-53"></div>
      <div className="container mx-auto px-6 relative z-10" data-id="element-54">
        <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-55">
          <div className="md:w-2/5" data-id="element-56">
            {/* Interactive image with 3D effect */}
            <div ref={imageRef} className="about-image max-w-md mx-auto relative overflow-hidden group interface-border" onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-57">
              <div className="relative rounded-lg overflow-hidden" data-id="element-58">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sb6h6Ejben2GShAZPsdmoM/orchid_1.jpg" alt="Orchid Reference" className="w-full h-auto transition-all duration-700 filter contrast-150 brightness-90 group-hover:brightness-110" data-id="element-59" />
                {/* Scan effect */}
                <div ref={scanRef} className="scan-effect" data-id="element-60"></div>
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent opacity-50" data-id="element-61"></div>
                {/* Scan line effect */}
                <div className="absolute inset-0 bg-[linear-gradient(transparent_50%,rgba(16,22,36,0.2)_50%)] bg-[size:100%_4px] opacity-20 group-hover:opacity-40 transition-opacity duration-500 animate-[scanlines_8s_linear_infinite]" data-id="element-62"></div>
                {/* Interactive glow on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-30 bg-gradient-to-r from-[#6ff8f0] to-[#2a4d7f] mix-blend-overlay transition-opacity duration-500" data-id="element-63"></div>
                {/* Ethereal glow effect */}
                <div className="about-glow" data-id="element-64"></div>
                {/* Technical data points */}
                <div className="tech-data-point tech-data-1" data-id="element-65">NEURAL: 97.3%</div>
                <div className="tech-data-point tech-data-2" data-id="element-66">
                  STATUS: ACTIVE
                </div>
                <div className="tech-data-point tech-data-3" data-id="element-67">SYNC: OPTIMAL</div>
              </div>
              {/* Border effects */}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-[#6ff8f0]/50 to-[#2a4d7f]/50 opacity-0 group-hover:opacity-100 blur-sm transition-opacity duration-500 -z-10" data-id="element-68"></div>
              {/* Data points */}
              <div className="absolute bottom-4 left-4 right-4 flex justify-between" data-id="element-69">
                <div className="glass px-2 py-1 text-xs text-[#6ff8f0] font-mono opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-70">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#6ff8f0] animate-pulse mr-2" data-id="element-71"></span>
                  ACTIVE
                </div>
                <div className="glass px-2 py-1 text-xs text-[#6ff8f0] font-mono opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-72">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#6ff8f0] animate-pulse mr-2" data-id="element-73"></span>
                  SPECIMEN
                </div>
              </div>
            </div>
          </div>
          <div className="md:w-3/5" data-id="element-74">
            {/* Clay Bust */}
            <div className="clay-bust-container" onMouseEnter={() => onCursorChange('image')} onMouseLeave={() => onCursorChange('default')} data-id="element-75">
              {/* Halo */}
              <div className="entity-halo" data-id="element-76"></div>
              {/* Main entity body */}
              <div className="entity-base" data-id="element-77">
                <div className="holographic-glow" data-id="element-78"></div>
                {/* Energy lines */}
                <div className="energy-line pink-1" data-id="element-79"></div>
                <div className="energy-line pink-2" data-id="element-80"></div>
                <div className="energy-line blue" data-id="element-81"></div>
                {/* Energy circles */}
                <div className="energy-circle circle-1" data-id="element-82"></div>
                <div className="energy-circle circle-2" data-id="element-83"></div>
              </div>
              {/* Entity head */}
              <div className="entity-head" data-id="element-84">
                <div className="holographic-glow" data-id="element-85"></div>
                <div className="energy-line pink-1" style={{
                height: '70%',
                left: '40%'
              }} data-id="element-86"></div>
                <div className="energy-line blue" style={{
                height: '60%',
                left: '60%'
              }} data-id="element-87"></div>
              </div>
              {/* Wings */}
              <div className="entity-wing left" data-id="element-88">
                {Array.from({
                length: 12
              }).map((_, i) => <div key={`left-feather-${i}`} className="entity-feather" style={{
                width: `${20 + i * 1.5}px`,
                top: `${i * 6}px`,
                left: '5px',
                transform: `rotate(${-10 + i * 2}deg)`
              }} data-id="element-89"></div>)}
              </div>
              <div className="entity-wing right" data-id="element-90">
                {Array.from({
                length: 12
              }).map((_, i) => <div key={`right-feather-${i}`} className="entity-feather" style={{
                width: `${20 + i * 1.5}px`,
                top: `${i * 6}px`,
                right: '5px',
                transform: `rotate(${10 - i * 2}deg)`
              }} data-id="element-91"></div>)}
              </div>
              {/* Wolf essence */}
              <div className="wolf-essence" data-id="element-92">
                <div className="holographic-glow" data-id="element-93"></div>
                <div className="wolf-head" data-id="element-94"></div>
                <div className="energy-line pink-1" style={{
                height: '80%',
                left: '30%'
              }} data-id="element-95"></div>
                <div className="energy-circle" style={{
                width: '8px',
                height: '8px',
                top: '50%',
                left: '60%'
              }} data-id="element-96"></div>
              </div>
              {/* Spear/staff */}
              <div className="entity-spear" data-id="element-97">
                <div className="spear-tip" data-id="element-98"></div>
              </div>
              {/* Floating particles */}
              {Array.from({
              length: 8
            }).map((_, i) => <div key={`particle-${i}`} className="entity-particle" style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              '--x': `${Math.random() * 60 - 30}px`,
              '--y': `${Math.random() * 60 - 30}px`,
              animationDuration: `${5 + Math.random() * 5}s`,
              animationDelay: `${Math.random() * 5}s`,
              opacity: Math.random() * 0.5 + 0.3
            }} data-id="element-99"></div>)}
            </div>
            <h2 ref={el => textRefs.current[0] = el} className="about-text text-3xl md:text-4xl font-bold mb-4 text-gradient font-heading text-center md:text-left" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-100">
              ENTITY
            </h2>
            <p ref={el => textRefs.current[1] = el} className="about-text text-[#a0c0e0] mb-3 font-mono leading-tight" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-101">
              I extract truth at the boundaries of flesh and machine. My work
              dissects symbiosis between biological systems and artificial
              constructs.
            </p>
            <p ref={el => textRefs.current[2] = el} className="about-text text-[#a0c0e0] mb-5 font-mono leading-tight" onMouseEnter={() => onCursorChange('text')} onMouseLeave={() => onCursorChange('default')} data-id="element-102">
              NYC-based, I probe the space between natural and engineered.
              Through{' '}
              <a href="#" className="text-[#6ff8f0] hover:underline relative group" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('text')} data-id="element-103">
                Yacht Labs
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#6ff8f0] group-hover:w-full transition-all duration-300" data-id="element-104"></span>
              </a>
              , I cultivate organisms that challenge conventional taxonomies.
            </p>
            <div ref={el => textRefs.current[3] = el} className="about-text flex flex-wrap justify-center md:justify-start gap-2" data-id="element-105">
              {['DISTORTION', 'MUTATION', 'CORRUPTION', 'SYNTHESIS', 'DECAY'].map((tag, index) => <span key={index} className="tag px-3 py-1 glass text-[#6ff8f0] text-sm font-mono 
                    border border-[#6ff8f0]/30 transition-all duration-300 
                    hover:border-[#6ff8f0] hover:bg-[#6ff8f0]/10 transform hover:-translate-y-1" style={{
              '--index': index
            }} ref={el => {
              if (el) el.classList.add('about-text');
            }} onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-106">
                  {tag}
                </span>)}
            </div>
            {/* Notable Endorsements */}
            <div id="about-endorsements" ref={el => textRefs.current[4] = el} className="about-text mt-10 pt-6 border-t border-[#1a2a40]/30" data-id="element-107">
              <h3 className="text-xl font-bold mb-4 text-gradient-purple font-heading" data-id="element-108">
                NOTABLE ENDORSEMENTS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-109">
                <div className="glass p-4 border border-[#1a2a40]/50 rounded-md" data-id="element-110">
                  <p className="text-[#a0c0e0] italic font-mono text-sm mb-2" data-id="element-111">
                    "A visionary at the intersection of biology and technology.
                    CHERYL's work challenges our understanding of what's
                    possible."
                  </p>
                  <div className="flex items-center" data-id="element-112">
                    <span className="text-[#6ff8f0] font-mono text-xs" data-id="element-113">
                      — Andrew Ng, Founder of DeepLearning.AI
                    </span>
                  </div>
                </div>
                <div className="glass p-4 border border-[#1a2a40]/50 rounded-md" data-id="element-114">
                  <p className="text-[#a0c0e0] italic font-mono text-sm mb-2" data-id="element-115">
                    "The biological distortions created here represent a new
                    frontier in artistic expression. Truly groundbreaking."
                  </p>
                  <div className="flex items-center" data-id="element-116">
                    <span className="text-[#6ff8f0] font-mono text-xs" data-id="element-117">
                      — Satya Nadella, Chairman and CEO of Microsoft
                    </span>
                  </div>
                </div>
                <div className="glass p-4 border border-[#1a2a40]/50 rounded-md" data-id="element-118">
                  <p className="text-[#a0c0e0] italic font-mono text-sm mb-2" data-id="element-119">
                    "CHERYL's work transcends conventional boundaries. A
                    profound reimagining of the relationship between humans and
                    technology."
                  </p>
                  <div className="flex items-center" data-id="element-120">
                    <span className="text-[#6ff8f0] font-mono text-xs" data-id="element-121">
                      — Deepak Chopra, Founder of Cyberhuman AI Inc.
                    </span>
                  </div>
                </div>
                <div className="glass p-4 border border-[#1a2a40]/50 rounded-md" data-id="element-122">
                  <p className="text-[#a0c0e0] italic font-mono text-sm mb-2" data-id="element-123">
                    "An extraordinary talent pushing the limits of what we
                    thought possible. The future of biotech art is here."
                  </p>
                  <div className="flex items-center" data-id="element-124">
                    <span className="text-[#6ff8f0] font-mono text-xs" data-id="element-125">
                      — Arianna Huffington, Founder and CEO of Thrive Global
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Industry Collaborators */}
            <div id="about-collaborators" ref={el => textRefs.current[5] = el} className="about-text mt-8" data-id="element-126">
              <h4 className="text-sm uppercase text-[#a0c0e0] font-mono mb-3" data-id="element-127">
                INDUSTRY COLLABORATORS
              </h4>
              <div className="flex flex-wrap gap-3" data-id="element-128">
                <a href="https://simonsinek.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-129">
                  Simon Sinek
                </a>
                <span className="text-xs text-[#a0c0e0] font-mono" data-id="element-130">•</span>
                <a href="https://www.linkedin.com/in/jeffweiner08" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-131">
                  Jeff Weiner
                </a>
                <span className="text-xs text-[#a0c0e0] font-mono" data-id="element-132">•</span>
                <a href="https://garyvaynerchuk.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-133">
                  Gary Vaynerchuk
                </a>
                <span className="text-xs text-[#a0c0e0] font-mono" data-id="element-134">•</span>
                <a href="https://www.danielgoleman.info" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-135">
                  Daniel Goleman
                </a>
                <span className="text-xs text-[#a0c0e0] font-mono" data-id="element-136">•</span>
                <a href="https://adamgrant.net" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-137">
                  Adam Grant
                </a>
                <span className="text-xs text-[#a0c0e0] font-mono" data-id="element-138">•</span>
                <a href="https://brenebrown.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-139">
                  Brené Brown
                </a>
                <span className="text-xs text-[#a0c0e0] font-mono" data-id="element-140">•</span>
                <a href="https://www.alliekmiller.com" target="_blank" rel="noopener noreferrer" className="text-xs text-[#e64cff] font-mono hover:text-[#ff80ff] transition-colors duration-300" onMouseEnter={() => onCursorChange('button')} onMouseLeave={() => onCursorChange('default')} data-id="element-141">
                  Allie K. Miller
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};